import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrev,
} from "@/components/ai-elements/inline-citation";
import { MessageResponse } from "@/components/ai-elements/message";
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from "@/components/ai-elements/sources";
import { HermesKbSourcePreviewDialog } from "@/components/hermes/HermesKbSourcePreviewDialog";
import {
  citationBadgeLabel,
  findSourceByNumber,
  hasInlineCitationMarkers,
  isInternalCitationSource,
  parseHermesCiteHref,
  parseHermesSources,
  rewriteCitationMarkersForMarkdown,
  type HermesCitationSource,
} from "@/lib/hermes-citations";
import type { HermesMessage } from "@/lib/hermes-api";
import { cn } from "@/lib/utils";
import {
  Component,
  useMemo,
  useState,
  type ComponentProps,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { toast } from "sonner";

/** Isolates citation UI crashes so session switch still shows message text. */
class CitationsSafeBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[HermesMessageCitations]", error, info.componentStack);
  }

  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

const BODY_CLASS =
  "text-sm leading-relaxed [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_pre]:my-3 [&_table]:text-sm [&_h1]:mt-4 [&_h2]:mt-4 [&_h3]:mt-3 [&_p]:my-2";

async function copyCitationUrl(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("已复制");
  } catch {
    toast.error("复制失败");
  }
}

function CitationHoverSource({ source }: { source: HermesCitationSource }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isKb = source.kind === "kb";
  const isInternal = isInternalCitationSource(source);
  const description = source.description ?? undefined;

  return (
    <div className="space-y-1">
      {isInternal ? (
        <>
          <button
            type="button"
            className="block w-full truncate text-left font-medium text-sm leading-tight text-foreground hover:underline"
            onClick={() => setPreviewOpen(true)}
          >
            {source.title}
          </button>
          <HermesKbSourcePreviewDialog
            source={source}
            open={previewOpen}
            onOpenChange={setPreviewOpen}
          />
        </>
      ) : (
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate font-medium text-sm leading-tight text-foreground hover:underline"
        >
          {source.title}
        </a>
      )}
      {/* KB 悬停卡用 Wiki 名作次要信息；勿展示丑陋的内部 path 当作「站点」 */}
      {!isKb && !isInternal ? (
        <button
          type="button"
          className="block w-full cursor-pointer truncate break-all text-left text-muted-foreground text-xs hover:text-foreground"
          title="点击复制链接"
          onClick={() => void copyCitationUrl(source.url)}
        >
          {source.url}
        </button>
      ) : null}
      {description ? (
        <p className="line-clamp-3 text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>
      ) : null}
    </div>
  );
}

/** 底部「使用了 N 个来源」列表行：引用序号 + 标题 / 网址两行 */
function CitationSourceListItem({ source }: { source: HermesCitationSource }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const href = source.url;
  const isKb = source.kind === "kb";
  const isInternal = isInternalCitationSource(source);
  const title = source.title?.trim() || href;
  // Web：第二行网址；KB：Wiki 名（勿把内部 path 当网址糊上去）
  const subtitle = isKb
    ? source.description?.trim() || null
    : href;
  const number = source.number?.trim();

  const row = (
    <>
      {/* 序号固定列 + 右侧文字栏，使标题与第二行网址左对齐 */}
      <span className="flex min-w-0 flex-1 items-start gap-1.5">
        {number ? (
          <span className="shrink-0 pt-px text-sm font-medium leading-tight text-muted-foreground tabular-nums">
            {number}.
          </span>
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-sm font-medium leading-tight text-foreground">
            {title}
          </span>
          {subtitle ? (
            <span className="truncate text-xs leading-tight text-muted-foreground">
              {subtitle}
            </span>
          ) : null}
        </span>
      </span>
    </>
  );

  if (isInternal) {
    return (
      <>
        <button
          type="button"
          className={cn(
            "flex w-full max-w-full items-start gap-2 rounded-md py-1 text-left no-underline hover:bg-muted/50",
          )}
          onClick={() => setPreviewOpen(true)}
        >
          {row}
        </button>
        <HermesKbSourcePreviewDialog
          source={source}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      </>
    );
  }

  return (
    <Source
      href={href}
      className="w-full max-w-full rounded-md py-1 no-underline hover:bg-muted/50"
      rel="noopener noreferrer"
      target="_blank"
    >
      {row}
    </Source>
  );
}

function CitationPill({ source }: { source: HermesCitationSource }) {
  const badge = citationBadgeLabel(source);

  return (
    <InlineCitation>
      <InlineCitationCard>
        <InlineCitationCardTrigger sources={[source.url]} label={badge} />
        <InlineCitationCardBody>
          <InlineCitationCarousel>
            <InlineCitationCarouselHeader>
              <InlineCitationCarouselPrev />
              <InlineCitationCarouselNext />
              <InlineCitationCarouselIndex />
            </InlineCitationCarouselHeader>
            <InlineCitationCarouselContent>
              <InlineCitationCarouselItem>
                <CitationHoverSource source={source} />
              </InlineCitationCarouselItem>
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
}

function CiteLink({
  href,
  sources,
  children,
  ...props
}: ComponentProps<"a"> & { sources: HermesCitationSource[] }) {
  const number = parseHermesCiteHref(href);
  if (number) {
    const source = findSourceByNumber(sources, number);
    if (source) return <CitationPill source={source} />;
    return <span>[{number}]</span>;
  }

  const isInternal = typeof href === "string" && href.startsWith("/");
  return (
    <a
      href={href}
      rel={isInternal ? undefined : "noreferrer"}
      target={isInternal ? undefined : "_blank"}
      {...props}
    >
      {children}
    </a>
  );
}

function PlainMessageBody({
  content,
  isStreaming,
}: {
  content: string;
  isStreaming: boolean;
}) {
  return (
    <MessageResponse
      className={BODY_CLASS}
      isAnimating={isStreaming}
      mode={isStreaming ? "streaming" : "static"}
    >
      {content}
    </MessageResponse>
  );
}

function CitedMessageBody({
  content,
  sources,
  isStreaming,
}: {
  content: string;
  sources: HermesCitationSource[];
  isStreaming: boolean;
}) {
  const useInline =
    !isStreaming && sources.length > 0 && hasInlineCitationMarkers(content);

  const rewritten = useMemo(
    () => (useInline ? rewriteCitationMarkersForMarkdown(content) : content),
    [content, useInline],
  );

  const components = useMemo(
    () =>
      useInline
        ? {
            a: (props: ComponentProps<"a">) => (
              <CiteLink {...props} sources={sources} />
            ),
          }
        : undefined,
    [sources, useInline],
  );

  if (!useInline) {
    return <PlainMessageBody content={content} isStreaming={isStreaming} />;
  }

  return (
    <MessageResponse
      className={BODY_CLASS}
      mode="static"
      components={components}
    >
      {rewritten}
    </MessageResponse>
  );
}

export function HermesMessageCitations({
  message,
  isStreaming = false,
}: {
  message: HermesMessage;
  isStreaming?: boolean;
}) {
  const sources = parseHermesSources(message.tool_payload);
  const content = message.content?.trim() ?? "";
  if (!content && sources.length === 0) return null;

  const plainFallback = content ? (
    <PlainMessageBody content={content} isStreaming={isStreaming} />
  ) : null;

  return (
    <CitationsSafeBoundary key={message.id} fallback={plainFallback}>
      <div className="flex w-full flex-col gap-2">
        {content ? (
          <CitedMessageBody
            content={content}
            sources={sources}
            isStreaming={isStreaming}
          />
        ) : null}

        {sources.length > 0 ? (
          <Sources className="mb-0 mt-1 text-muted-foreground">
            <SourcesTrigger count={sources.length} />
            <SourcesContent className="w-full max-w-full">
              {sources.map((source) => (
                <CitationSourceListItem
                  key={`${source.number}-${source.url}`}
                  source={source}
                />
              ))}
            </SourcesContent>
          </Sources>
        ) : null}
      </div>
    </CitationsSafeBoundary>
  );
}
